import React from 'react';
import { FiAlertTriangle, FiX } from 'react-icons/fi';

/**
 * Confirmation modal for character deletion.
 * Warns the user that all generated photos will also be permanently deleted.
 *
 * Props:
 *   isOpen        - boolean
 *   character     - { characterId, name } object of the character being deleted
 *   onConfirm     - () => void  called when user confirms deletion
 *   onCancel      - () => void  called when user cancels
 *   isDeleting    - boolean  shows spinner / disables buttons while request is in-flight
 */
const DeleteCharacterModal = ({ isOpen, character, onConfirm, onCancel, isDeleting = false }) => {
  if (!isOpen || !character) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative">
        {/* Close button */}
        <button
          onClick={onCancel}
          disabled={isDeleting}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-40"
          aria-label="Cancel"
        >
          <FiX size={20} />
        </button>

        {/* Icon + title */}
        <div className="flex flex-col items-center text-center gap-3 mb-5">
          <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center">
            <FiAlertTriangle size={28} className="text-red-500" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">
            Delete &ldquo;{character.name}&rdquo;?
          </h2>
        </div>

        {/* Warning body */}
        <p className="text-sm text-gray-600 text-center mb-2">
          This will permanently delete the character and{' '}
          <span className="font-semibold text-gray-800">all photos generated for them</span>,
          including images stored in the cloud.
        </p>
        <p className="text-sm text-gray-500 text-center mb-6">
          This action cannot be undone.
        </p>

        {/* Action buttons */}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={isDeleting}
            className="flex-1 py-2.5 px-4 rounded-xl border border-gray-200 text-gray-700 font-medium text-sm hover:bg-gray-50 transition-colors disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="flex-1 py-2.5 px-4 rounded-xl bg-red-500 text-white font-medium text-sm hover:bg-red-600 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {isDeleting ? (
              <>
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Deleting…
              </>
            ) : (
              'Delete Character'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteCharacterModal;
